import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Blocks all navigation attempts. This is useful for preventing the page from
 * changing until some condition is met, like saving form data.
 *
 * @param  blocker - Function that receives the navigation transaction
 * @param  when - Boolean to enable/disable blocking
 * @see https://reactrouter.com/en/main/hooks/use-blocker (v6.4+)
 */
export default function useBlocker(blocker, when = true) {
  const navigationContext = useContext(NavigationContext);
  const blockerRef = useRef(blocker);

  // Keep blocker ref updated
  useEffect(() => {
    blockerRef.current = blocker;
  }, [blocker]);

  useEffect(() => {
    if (!when) return;

    // Safely get navigator - check if it exists and has block method
    // navigationContext.navigator is different from window.navigator
    const routerNavigator = navigationContext?.navigator;

    if (!routerNavigator) {
      console.warn('[useBlocker] No navigator found in NavigationContext');
      return;
    }

    // Check if navigator.block exists and is actually a function
    if (typeof routerNavigator.block === 'function') {
      const unblock = routerNavigator.block((tx) => {
        const autoUnblockingTx = {
          ...tx,
          retry() {
            unblock();
            tx.retry();
          },
        };

        blockerRef.current(autoUnblockingTx);
      });

      return unblock;
    } else {
      console.debug('[useBlocker] navigator.block not available in this React Router version');
    }
  }, [navigationContext, when]);
}
